import { SettingScopesEnum } from "@simbelapp/order-sdk";
import useVuelidate, { type ValidationRule } from "@vuelidate/core";
import { email, helpers, required, type MessageProps } from "@vuelidate/validators";
import { storeToRefs } from "pinia";
import { useCreateProjectStore } from "~/store/project/create-project.store";
import { ItemType } from "~/utils/enums/common.enums";
import type { IHeaderNav, IIndexHeader } from "~/utils/interfaces/table-interfaces";
import { useAuthStore } from "~/store/auth/auth.store";
import { useCompaniesStore } from "~/store/companies/companies.store";
import { useCreateUserStore } from "~/store/users/create-users.store";
import { useUsersStore } from "~/store/users/users.store";
import { userDisplayName } from "~/utils/helpers/users/company-user";
import { useUserDetailStore } from "~/store/users/user-detail.store";
import { ColorsTypeEnum } from "~/utils/enums/colores.enums";
import { CompanyIdentificationModeEnum } from "~/utils/enums/companies.enums";
import { ProjectTypeEnum } from "~/utils/enums/project.enums";
import { UserStatusEnum, UnavailableUsersStatusEnum } from "~/utils/enums/users.enums";
import type { IFooterActions } from "~/utils/interfaces/common-interfaces";
import type { IUserDetails } from "~/utils/interfaces/users-interfaces";

export const useUsers = () => {
  const headers = useState<IIndexHeader[]>(() => [
    {
      name: "Nom",
      sort: true,
      key: "lastname",
      minWidthPx: 180,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.UsersTab,
    },
    {
      name: "Email",
      sort: true,
      key: "email",
      minWidthPx: 327,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.UsersTab,
    },
    {
      name: "Matricule",
      sort: true,
      key: "employee_number",
      minWidthPx: 180,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.UsersTab,
    },
    {
      name: "Entité légale",
      sort: true,
      key: "legal_entity_name",
      minWidthPx: 140,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.UsersTab,
    },
    {
      name: "Statut",
      sort: true,
      key: "status",
      minWidthPx: 140,
      maxWidthFr: 1,
      visible: false,
      scope: SettingScopesEnum.UsersTab,
    },
    {
      name: "Équipe",
      sort: true,
      key: "team",
      minWidthPx: 155,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.UsersTab,
    },
    {
      name: "Intitulé de poste",
      sort: true,
      key: "job",
      minWidthPx: 140,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.UsersTab,
    },
    {
      name: "Groupe",
      sort: false,
      key: "group",
      minWidthPx: 155,
      maxWidthFr: 1,
      visible: false,
      scope: SettingScopesEnum.UsersTab,
    },
    {
      name: "Manager",
      sort: true,
      key: "manager_lastname",
      minWidthPx: 213,
      maxWidthFr: 1,
      visible: false,
      scope: SettingScopesEnum.UsersTab,
    },
    {
      name: "Catégorie socioprofessionnelle",
      sort: true,
      key: "socio_professional_category",
      minWidthPx: 140,
      maxWidthFr: 1,
      visible: false,
      scope: SettingScopesEnum.UsersTab,
    },
    {
      name: "Business Unit",
      sort: true,
      key: "business_unit",
      minWidthPx: 140,
      maxWidthFr: 1,
      visible: false,
      scope: SettingScopesEnum.UsersTab,
    },
    {
      name: "Rôle",
      sort: true,
      key: "roles",
      minWidthPx: 140,
      maxWidthFr: 1,
      visible: false,
      scope: SettingScopesEnum.UsersTab,
    },
    {
      name: "Date de début de contrat",
      sort: true,
      key: "contract_start_date",
      minWidthPx: 200,
      maxWidthFr: 1,
      visible: false,
      scope: SettingScopesEnum.UsersTab,
    },
    {
      name: "Type de contrat",
      sort: true,
      key: "contract_type",
      minWidthPx: 140,
      maxWidthFr: 1,
      visible: false,
      scope: SettingScopesEnum.UsersTab,
    },
  ]);
  const unavailableUsersHeaders = useState<IIndexHeader[]>(() => [
    {
      name: "Nom",
      sort: true,
      key: "lastname",
      minWidthPx: 180,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.UsersTab,
    },
    {
      name: "Email",
      sort: true,
      key: "email",
      minWidthPx: 140,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.UsersTab,
    },
    {
      name: "Indisponibilité",
      sort: false,
      key: "indisponibility",
      minWidthPx: 80,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.UsersTab,
    },
    {
      name: "Détail Indisponibilité",
      sort: false,
      key: "indisponibility_detail",
      minWidthPx: 140,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.UsersTab,
    },
    {
      name: "Matricule",
      sort: true,
      key: "employee_number",
      minWidthPx: 80,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.UsersTab,
    },
    {
      name: "Equipe",
      sort: true,
      key: "team",
      minWidthPx: 80,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.UsersTab,
    },
  ]);

  function getNav(): IHeaderNav[] {
    return [];
  }

  let footersActions: IFooterActions[];
  const authStore = useAuthStore();

  const companiesStore = useCompaniesStore();

  companiesStore.fetchCompanyInfos();
  const { companyInfos } = storeToRefs(companiesStore);

  if (authStore.isCompanyAdmin || authStore.isGroupAdmin) {
    footersActions = [
      {
        name: "Groupe",
        logoName: "invite",
        action: showGroupsModal,
      },
      {
        name: "Contacter",
        logoName: "message",
        action: showNotifyModal,
      },
      {
        name: "Ajouter au plan",
        logoName: "create",
        action: showUserManagement,
      },
    ];

    if (companyInfos.value?.authStrategy === "sso_saml") {
      footersActions.push({
        name: "SSO Actif",
        logoName: "lock",
        action: disableSSO,
      });
    }
  } else {
    footersActions = [
      {
        name: "Contacter",
        logoName: "message",
        action: showNotifyModal,
      },
    ];
  }

  async function showUserManagement(selectedCount: number) {
    const createProjectCoreStore = useCreateProjectStore();
    if (!createProjectCoreStore.canAddToPlan(selectedCount)) return;
    const { project } = storeToRefs(createProjectCoreStore);
    const usersStore = useUsersStore();
    const resolvesSelectAll = usersStore.excludedUsersIdList.length > 0 || !usersStore.selectedUsersIdList.length;
    // A failed lookup has already shown its error: don't open the funnel on an empty selection.
    if (resolvesSelectAll && !(await usersStore.fetchNotExcludedUser(usersStore.excludedUsersIdList))) return;

    createProjectCoreStore.openProjectModal();
    project.value.projectType = ProjectTypeEnum.NEW_PROJECT;
    project.value.sourceType = ItemType.USER;
    project.value.users.usersId = usersStore.selectedUsersIdList.map((u) => {
      return {
        userId: u.userId,
        id: project.value.projectId,
        firstname: u.firstname ?? null,
        lastname: u.lastname ?? null,
        email: u.email ?? null,
        displayValue: userDisplayName(u),
        managerEmail: u.managerEmail ?? null,
        managerDisplayValue:
          u.managerDisplayValue ??
          (u.managerFirstname && u.managerLastname ? `${u.managerFirstname} ${u.managerLastname}` : null),
      };
    });
  }

  function showGroupsModal() {
    const userStore = useUsersStore();
    userStore.showGroupsUsersModal = true;
  }

  function showNotifyModal() {
    const userStore = useUsersStore();
    userStore.showNotifyUsers = true;
  }

  function disableSSO() {
    const userStore = useUsersStore();
    userStore.showDisableSsoModal = true;
  }

  function statusMapping(value: string): {
    name: string;
    value: string;
    type: ColorsTypeEnum;
    logo: string | null;
  } {
    return (
      [
        {
          name: UserStatusEnum.DISABLED,
          value: "Désactivé",
          type: ColorsTypeEnum.LIGHT_RED,
          logo: null,
        },
        {
          name: UserStatusEnum.UNREGISTERED,
          value: "Actif",
          type: ColorsTypeEnum.LIGHT_GREEN,
          logo: null,
        },
        {
          name: UserStatusEnum.REGISTERED,
          value: "Actif",
          type: ColorsTypeEnum.LIGHT_GREEN,
          logo: null,
        },
      ].find((x) => x.name === value) ?? {
        name: UserStatusEnum.REGISTERED,
        value: "Actif",
        type: ColorsTypeEnum.LIGHT_GREEN,
        logo: null,
      }
    );
  }
  function unavailableUsersStatusMapping(value: UnavailableUsersStatusEnum): {
    name: UnavailableUsersStatusEnum;
    value: string;
    type: ColorsTypeEnum;
    logo: string | null;
  } {
    return (
      [
        {
          name: UnavailableUsersStatusEnum.SIMBEL,
          value: "Session Simbel",
          type: ColorsTypeEnum.LIGHT_BLUE,
          logo: null,
        },
        {
          name: UnavailableUsersStatusEnum.SFTP,
          value: "Agenda externe",
          type: ColorsTypeEnum.LIGHT_PURPLE,
          logo: null,
        },
      ].find((x) => x.name === value) ?? {
        name: UnavailableUsersStatusEnum.SFTP,
        value: "Agenda externe",
        type: ColorsTypeEnum.LIGHT_PURPLE,
        logo: null,
      }
    );
  }

  return {
    headers,
    unavailableUsersHeaders,
    footersActions,
    statusMapping,
    unavailableUsersStatusMapping,
    getNav,
  };
};

export const useUsersValidation = () => {
  const createUserStore = useCreateUserStore();

  const employeesNumbersValidationRule: ValidationRule<MessageProps> = (value, validationParams) => {
    const employeesNumbers: string = value as unknown as string;
    let employeesNumbersList = employeesNumbers.split(/,| +|;/).filter((item) => item !== "");
    employeesNumbersList = employeesNumbersList.map((employeeNumber) => employeeNumber.trim());

    if (hasDuplicates(employeesNumbersList)) {
      createUserStore.employeesNumberListError = "Matricule employé dupliqué";

      return validationParams.message;
    }
    createUserStore.employeesNumberListError = null;

    return null;
  };

  async function validateUsersCreation() {
    const companiesStore = useCompaniesStore();
    const { users } = storeToRefs(createUserStore);

    const mandatoryFieldMessage = "Ce champ est obligatoire";

    const sharedRules = {
      language: {
        required: helpers.withMessage(mandatoryFieldMessage, required),
      },
      legalEntity: {
        required: helpers.withMessage(mandatoryFieldMessage, required),
      },
      manager: {},
    };

    let rules: any;
    if (
      companiesStore.companyInfos?.identificationMode &&
      companiesStore.companyInfos?.identificationMode === CompanyIdentificationModeEnum.EMAIL
    ) {
      const invalidEmails = users.value.emailsList ? validateUserEmailsList(users.value.emailsList) : null;

      rules = computed(() => {
        return {
          emailsList: {
            required: helpers.withMessage(mandatoryFieldMessage, required),
            email: helpers.withMessage(`Email(s) invalides: ${invalidEmails} `, () => !invalidEmails),
          },
          ...sharedRules,
        };
      });
    } else {
      const invalidEmployeeNumber = "Matricule employé dupliqué";

      rules = computed(() => {
        return {
          employeesNumbers: {
            required: helpers.withMessage(mandatoryFieldMessage, required),
            employeesNumbers: helpers.withMessage(invalidEmployeeNumber, employeesNumbersValidationRule),
          },
          ...sharedRules,
        };
      });
    }

    const v$ = useVuelidate(rules, users);
    await v$.value.$validate();

    return v$;
  }

  function validateUserEmailsList(emailsList: string): string {
    const createUserStore = useCreateUserStore();
    const valideEmails = createUserStore.extractEmailsFromText(emailsList);
    if (valideEmails?.length) {
      valideEmails.forEach((e) => (emailsList = emailsList.replaceAll(e, "").trim()));
      emailsList = emailsList?.replace(/[,;\s]/gi, "");
      createUserStore.emailsListError = null;
      createUserStore.employeesNumberListError = null;
      return emailsList;
    } else {
      return emailsList;
    }
  }

  function hasDuplicates<T>(arr: T[]): boolean {
    return new Set(arr).size !== arr.length;
  }

  async function validateUserUpdate() {
    const userStore = useUserDetailStore();
    const companiesStore = useCompaniesStore();

    const { userDetails: userDetailsRef } = storeToRefs(userStore);
    const { companyInfos } = storeToRefs(companiesStore);

    const userDetails: IUserDetails = userDetailsRef.value!;

    const mandatoryFieldMessage = "Ce champ est obligatoire";
    const invalidEmail = "Email invalide";

    const rules = computed(() => {
      return {
        lastname: {
          required: helpers.withMessage(mandatoryFieldMessage, required),
        },
        firstname: {
          required: helpers.withMessage(mandatoryFieldMessage, required),
        },
        email: {
          required:
            companyInfos.value?.identificationMode === CompanyIdentificationModeEnum.EMAIL
              ? helpers.withMessage(mandatoryFieldMessage, required)
              : true,
          email: helpers.withMessage(invalidEmail, email),
        },
        employeeNumber: {
          required:
            companyInfos.value?.identificationMode === CompanyIdentificationModeEnum.EMPLOYEE_NUMBER
              ? helpers.withMessage(mandatoryFieldMessage, required)
              : true,
        },
        roles: {
          required: helpers.withMessage(mandatoryFieldMessage, required),
        },
      };
    });

    const v$ = useVuelidate(rules, userDetails);
    await v$.value.$validate();

    return v$;
  }

  return { validateUsersCreation, validateUserUpdate };
};
